import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { PLAN_FORM } from "../copy/opsDescriptions.js";

export default function CompensationPlanForm({
  open,
  onClose,
  onSubmit,
  loading = false,
  invoices = [],
}) {
  const defaultInvoiceId = invoices[0]?.id || "";
  const [form, setForm] = useState({
    invoiceId: defaultInvoiceId,
    totalAmount: "",
    reason: "",
    createProviderAdjustment: true,
    adjustmentResponsibility: "PROVIDER",
    installments: [
      { sequence: 1, amount: "", dueDate: "" },
      { sequence: 2, amount: "", dueDate: "" },
    ],
  });

  const updateInstallment = (index, field, value) => {
    setForm((prev) => {
      const next = [...prev.installments];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, installments: next };
    });
  };

  const addInstallment = () => {
    setForm((prev) => ({
      ...prev,
      installments: [
        ...prev.installments,
        { sequence: prev.installments.length + 1, amount: "", dueDate: "" },
      ],
    }));
  };

  const removeInstallment = (index) => {
    setForm((prev) => ({
      ...prev,
      installments: prev.installments
        .filter((_, i) => i !== index)
        .map((inst, i) => ({ ...inst, sequence: i + 1 })),
    }));
  };

  const handleSubmit = () => {
    onSubmit?.({
      invoiceId: form.invoiceId,
      totalAmount: Number(form.totalAmount),
      reason: form.reason.trim(),
      createProviderAdjustment: form.createProviderAdjustment,
      adjustmentResponsibility: form.adjustmentResponsibility,
      installments: form.installments.map((inst, i) => ({
        sequence: i + 1,
        amount: Number(inst.amount),
        dueDate: new Date(inst.dueDate).toISOString(),
      })),
    });
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose?.()} maxWidth="md" fullWidth>
      <DialogTitle>{PLAN_FORM.title}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
          {PLAN_FORM.intro}
        </Alert>
        <Stack spacing={2}>
          <TextField
            select
            label="Invoice"
            value={form.invoiceId}
            onChange={(e) => setForm((p) => ({ ...p, invoiceId: e.target.value }))}
            fullWidth
            required
          >
            {invoices.map((inv) => (
              <MenuItem key={inv.id} value={inv.id}>
                {inv.id} ({inv.status || "—"})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Total compensation (ZAR)"
            type="number"
            value={form.totalAmount}
            onChange={(e) => setForm((p) => ({ ...p, totalAmount: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Reason"
            value={form.reason}
            onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
            required
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={form.createProviderAdjustment}
                onChange={(e) =>
                  setForm((p) => ({ ...p, createProviderAdjustment: e.target.checked }))
                }
              />
            }
            label="Also create pending partner adjustment (approve separately to claw back on payout)"
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Installments (must sum to total)
          </Typography>
          {form.installments.map((inst, index) => (
            <Stack key={inst.sequence} direction="row" spacing={1} alignItems="center">
              <TextField
                label={`#${inst.sequence} amount`}
                type="number"
                size="small"
                value={inst.amount}
                onChange={(e) => updateInstallment(index, "amount", e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Due date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={inst.dueDate}
                onChange={(e) => updateInstallment(index, "dueDate", e.target.value)}
                sx={{ flex: 1 }}
              />
              {form.installments.length > 1 && (
                <IconButton size="small" onClick={() => removeInstallment(index)} aria-label="Remove">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
          <Button startIcon={<Add />} onClick={addInstallment} sx={{ alignSelf: "flex-start" }}>
            Add installment
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            loading ||
            !form.invoiceId ||
            !form.totalAmount ||
            !form.reason.trim() ||
            form.installments.some((i) => !i.amount || !i.dueDate)
          }
        >
          Create plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
